/* eslint-disable no-console */
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useAuthContext } from "../../context/auth";
import {
  actualizarOperacionFactoring,
  actualizarProveedorFactoring,
  anularOperacionFactoring,
  crearMoraFactoring,
  crearOperacionFactoringHistorica,
  desvincularComisionFactoring,
  eliminarOperacionFactoring,
  getComisionesFactoring,
  getFacturasComisionDisponibles,
  getOperacionesFactoring,
  getProveedoresFactoring,
  liquidarOperacionFactoring,
  vincularComisionFactoring,
  vincularFacturasAOperacion,
  vincularMovimientosAOperacion,
} from "../../utils/factoring";
import { getCuentasBancarias, getMovimientosBancarios } from "../../utils/movimientosBancarios";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import LiquidarFactoringModal from "../LiquidarFactoringModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import styles from "./Factoring.module.scss";

// RUT del proveedor emisor de la factura de comisión, por proveedor_factoring_id (son 3 factores
// activos en Chile). Se usa para filtrar las facturas SII recibidas candidatas a comisión.
const RUT_COMISION_POR_FACTOR = {
  1: "77078244", // Xepelin → X Capital SPA
  2: "76596744", // Chita SPA
  3: "97036000", // Finto / Santander
};

const MESES_NOMBRES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const ESTADO_OPCIONES = [
  { value: "todos", label: "Todos" },
  { value: "activa", label: "Activas" },
  { value: "liquidada", label: "Liquidadas" },
  { value: "anulada", label: "Anuladas" },
];

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [, m, d] = f.split("-");
  return `${d}/${m}`;
};

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  if (typeof value === "string") return value;
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const parseMontoInput = (displayValue, moneda) => {
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    numStr = moneda === "CLP" || moneda === "PEN" ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

// Modal inline para registrar mora sobre una operación liquidada.
const MoraModal = ({ isOpen, onClose, operacion, pais, userId, monedaPrincipal, onSuccess }) => {
  const hoy = new Date().toISOString().split("T")[0];
  const [montoMora, setMontoMora] = useState("");
  const [fechaNotificacion, setFechaNotificacion] = useState(hoy);
  const [notas, setNotas] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMontoMora("");
    setFechaNotificacion(hoy);
    setNotas("");
  }, [isOpen, hoy]);

  const handleRegistrar = async () => {
    if (!fechaNotificacion) return toast.error("Ingresá una fecha");
    const montoNum = typeof montoMora === "string" ? 0 : montoMora;
    if (!montoNum || montoNum <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setLoading(true);
    try {
      await crearMoraFactoring({
        operacion_factoring_id: operacion.id,
        pais,
        monto_mora: montoNum,
        fecha_notificacion: fechaNotificacion,
        notas: notas.trim() || null,
        created_by: userId || null,
      });
      toast.success("Mora registrada");
      onSuccess?.();
    } catch (err) {
      console.error("Error registrando mora:", err);
      toast.error("Error al registrar la mora");
    } finally {
      setLoading(false);
    }
  };

  if (!operacion) return null;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          Registrar mora — Folio {operacion.factura?.folio || "—"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Monto ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formatMontoDisplay(montoMora, pais)}
              onChange={(e) => setMontoMora(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha notificación
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaNotificacion}
              onChange={(e) => setFechaNotificacion(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            disabled={loading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleRegistrar} disabled={loading}>
          {loading ? "Registrando..." : "Registrar mora"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Modal inline para editar comisión proveedor, fecha de vencimiento,
// referencia del proveedor y giro depositado real de una operación activa.
const EditarOperacionModal = ({ isOpen, onClose, operacion, pais, monedaPrincipal, onSuccess, onComisionChange }) => {
  const { formatAmount } = useCurrency();
  const [comisionProveedor, setComisionProveedor] = useState("");
  const [fechaCobroFinal, setFechaCobroFinal] = useState("");
  const [referenciaProveedor, setReferenciaProveedor] = useState("");
  const [giroDepositado, setGiroDepositado] = useState("");
  const [loading, setLoading] = useState(false);

  // Sección "Factura de comisión del proveedor": comisiones ya vinculadas + candidatas disponibles.
  const [comisionesVinculadas, setComisionesVinculadas] = useState([]);
  const [facturasComisionDisponibles, setFacturasComisionDisponibles] = useState([]);
  const [busquedaComision, setBusquedaComision] = useState("");
  const [loadingComision, setLoadingComision] = useState(false);

  const rutFactor = operacion ? RUT_COMISION_POR_FACTOR[operacion.proveedor_factoring_id] : null;

  const cargarComisiones = async () => {
    if (!operacion) return;
    try {
      const [vinculadas, disponibles] = await Promise.all([
        getComisionesFactoring(operacion.id),
        getFacturasComisionDisponibles(pais, rutFactor),
      ]);
      setComisionesVinculadas(vinculadas);
      setFacturasComisionDisponibles(disponibles);
    } catch (err) {
      console.error("Error cargando comisiones:", err);
    }
  };

  useEffect(() => {
    if (!isOpen || !operacion) return;
    setComisionProveedor(Number(operacion.comision_proveedor || 0));
    setFechaCobroFinal(operacion.fecha_cobro_final || "");
    setReferenciaProveedor(operacion.referencia_proveedor || "");
    setGiroDepositado(operacion.giro_depositado ? Number(operacion.giro_depositado) : "");
    setBusquedaComision("");
    cargarComisiones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, operacion]);

  const handleVincularComision = async (facturaId) => {
    setLoadingComision(true);
    try {
      await vincularComisionFactoring(operacion.id, facturaId);
      toast.success("Comisión vinculada");
      await cargarComisiones();
      onComisionChange?.();
    } catch (err) {
      console.error("Error vinculando comisión:", err);
      toast.error("Error al vincular comisión");
    } finally {
      setLoadingComision(false);
    }
  };

  const handleDesvincularComision = async (filaId) => {
    setLoadingComision(true);
    try {
      await desvincularComisionFactoring(filaId);
      toast.success("Comisión desvinculada");
      await cargarComisiones();
      onComisionChange?.();
    } catch (err) {
      console.error("Error desvinculando comisión:", err);
      toast.error("Error al desvincular comisión");
    } finally {
      setLoadingComision(false);
    }
  };

  // Sugerencia: las facturas cuyo monto ≈ comisión proveedor (±$100 redondeo IVA) primero.
  const comisionRef = Number(operacion?.comision_proveedor || 0);
  const facturasComisionOrdenadas = [...facturasComisionDisponibles]
    .filter((f) => {
      if (!busquedaComision.trim()) return true;
      const q = busquedaComision.trim().toLowerCase();
      return (
        String(f.folio).includes(q) || String(Math.round(Number(f.monto_total) || 0)).includes(q.replace(/\D/g, ""))
      );
    })
    .sort((a, b) => {
      const da = Math.abs(Number(a.monto_total) - comisionRef);
      const db = Math.abs(Number(b.monto_total) - comisionRef);
      const sa = da <= 100 ? 0 : 1;
      const sb = db <= 100 ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (b.fecha_emision || "").localeCompare(a.fecha_emision || "");
    });

  const handleGuardar = async () => {
    const comisionNum = typeof comisionProveedor === "string" ? 0 : comisionProveedor;
    if (comisionNum < 0) {
      toast.error("Comisión inválida");
      return;
    }
    setLoading(true);
    try {
      await actualizarOperacionFactoring(operacion.id, {
        comision_proveedor: comisionNum,
        fecha_cobro_final: fechaCobroFinal || null,
        referencia_proveedor: referenciaProveedor.trim() || null,
        giro_depositado: giroDepositado !== "" ? Number(giroDepositado) : null,
      });
      toast.success("Operación actualizada");
      onSuccess?.();
    } catch (err) {
      console.error("Error actualizando operación:", err);
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  if (!operacion) return null;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          Editar operación — Folio {operacion.factura?.folio || "—"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Comisión proveedor ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formatMontoDisplay(comisionProveedor, pais)}
              onChange={(e) => setComisionProveedor(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha vencimiento
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fechaCobroFinal}
              onChange={(e) => setFechaCobroFinal(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Nº operación proveedor
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 406591"
              value={referenciaProveedor}
              onChange={(e) => setReferenciaProveedor(e.target.value)}
              disabled={loading}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Giro depositado real ({monedaPrincipal})
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Calculado: dejar vacío si coincide"
              value={formatMontoDisplay(giroDepositado, pais)}
              onChange={(e) => setGiroDepositado(parseMontoInput(e.target.value, monedaPrincipal))}
              disabled={loading}
            />
          </div>
        </div>

        {/* Factura de comisión del proveedor (DTE recibido neteado del giro). Vincular la(s)
            factura(s) la(s) saca de "Por pagar" y del ledger. Permite múltiples (comisión + interés). */}
        <div style={{ marginTop: 18, borderTop: "1px solid #eee", paddingTop: 12 }}>
          <label style={{ fontSize: "0.8rem", fontWeight: 700, display: "block", marginBottom: 6 }}>
            Factura de comisión del proveedor
          </label>

          {comisionesVinculadas.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {comisionesVinculadas.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 10px",
                    marginBottom: 4,
                    borderRadius: 4,
                    background: "#f0f9f4",
                    border: "1px solid #198754",
                    fontSize: "0.8rem",
                  }}
                >
                  <span style={{ color: "#198754", fontWeight: 600 }}>
                    ✓ Folio {c.factura?.folio ?? c.factura_sii_recibida_id}
                    {c.factura?.fecha_emision ? ` · ${formatFecha(c.factura.fecha_emision)}` : ""}
                    {c.factura?.monto_total != null ? ` · ${formatAmount(c.factura.monto_total, monedaPrincipal)}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDesvincularComision(c.id)}
                    disabled={loadingComision}
                    style={{
                      border: "1px solid #c62828",
                      background: "none",
                      borderRadius: 4,
                      color: "#c62828",
                      fontSize: "0.75rem",
                      padding: "2px 8px",
                      cursor: loadingComision ? "not-allowed" : "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {!rutFactor ? (
            <div style={{ fontSize: "0.78rem", color: "#999" }}>
              Proveedor de factoring sin RUT mapeado para sugerir facturas.
            </div>
          ) : (
            <>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por folio o monto..."
                value={busquedaComision}
                onChange={(e) => setBusquedaComision(e.target.value)}
                style={{ marginBottom: 6, fontSize: "0.82rem" }}
              />
              <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                {facturasComisionOrdenadas.length === 0 ? (
                  <div style={{ padding: 12, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
                    Sin facturas disponibles del proveedor
                  </div>
                ) : (
                  facturasComisionOrdenadas.map((f) => {
                    const sugerida = Math.abs(Number(f.monto_total) - comisionRef) <= 100;
                    return (
                      <div
                        key={f.id}
                        onClick={() => !loadingComision && handleVincularComision(f.id)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 10px",
                          borderBottom: "1px solid #f0f0f0",
                          fontSize: "0.8rem",
                          cursor: loadingComision ? "not-allowed" : "pointer",
                          background: sugerida ? "#f0f9f4" : "transparent",
                        }}
                      >
                        <span>
                          {formatFecha(f.fecha_emision)} · Folio {f.folio}
                          {sugerida && (
                            <span style={{ marginLeft: 6, color: "#198754", fontSize: "0.72rem", fontWeight: 600 }}>
                              ≈ comisión
                            </span>
                          )}
                        </span>
                        <span style={{ fontWeight: 600, color: "#c0392b" }}>
                          {formatAmount(f.monto_total, monedaPrincipal)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Modal inline para registrar una operación nueva o histórica (ya liquidada).
const NuevaOperacionModal = ({ isOpen, onClose, pais, userId, monedaPrincipal, onSuccess }) => {
  const { formatAmount } = useCurrency();
  const hoy = new Date().toISOString().split("T")[0];

  const [modo, setModo] = useState("activa"); // "activa" | "liquidada"
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [facturasSII, setFacturasSII] = useState([]);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [busquedaFactura, setBusquedaFactura] = useState("");
  const [mostrarMovimientos, setMostrarMovimientos] = useState(false);
  const [movsAdelanto, setMovsAdelanto] = useState([]);
  const [movsRemanente, setMovsRemanente] = useState([]);

  const [form, setForm] = useState({
    proveedor_factoring_id: "",
    referencia_proveedor: "",
    fecha_operacion: hoy,
    fecha_cobro_final: "",
    fecha_pago_real: "",
    pct_anticipo: "",
    tasa: "",
    tasa_mora: "",
    monto_factura: "",
    comision_proveedor: "",
    giro_depositado: "",
    cuenta_bancaria_id: "",
  });

  // Si hay facturas seleccionadas, monto es la suma — auto-calculado y read-only.
  const montoAutoCalculado =
    facturasSeleccionadas.length > 0
      ? facturasSeleccionadas.reduce((sum, f) => sum + Number(f.monto_total || 0), 0)
      : null;
  const montoF = montoAutoCalculado ?? (typeof form.monto_factura === "string" ? 0 : Number(form.monto_factura || 0));
  // parseFloat("1,85") devuelve 1 — coma corta el parsing. Normalizamos antes.
  const parsePct = (v) => parseFloat(String(v ?? "0").replace(",", ".")) / 100 || 0;
  const pctAnticipo = parsePct(form.pct_anticipo);
  const tasaF = parsePct(form.tasa);
  const tasaMoraF = parsePct(form.tasa_mora);
  const capitalAdelantado = Math.round(montoF * pctAnticipo);
  const excedentes = montoF - capitalAdelantado;
  const plazoDias = (() => {
    if (!form.fecha_cobro_final || !form.fecha_operacion) return 0;
    const d1 = new Date(form.fecha_operacion);
    const d2 = new Date(form.fecha_cobro_final);
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
  })();
  const diferenciaPrecio = Math.round(plazoDias * (tasaF / 30) * capitalAdelantado);
  const comisionProveedor = typeof form.comision_proveedor === "string" ? 0 : Number(form.comision_proveedor || 0);
  const totalCobros = diferenciaPrecio + comisionProveedor;
  const giroARecibir = capitalAdelantado - totalCobros;
  const fmt = (v) => formatAmount(v, monedaPrincipal);

  useEffect(() => {
    if (!isOpen || !pais) return;
    setForm({
      proveedor_factoring_id: "",
      referencia_proveedor: "",
      fecha_operacion: hoy,
      fecha_cobro_final: "",
      fecha_pago_real: "",
      pct_anticipo: "",
      tasa: "",
      tasa_mora: "",
      monto_factura: "",
      comision_proveedor: "",
      giro_depositado: "",
      cuenta_bancaria_id: "",
    });
    setModo("activa");
    setFacturasSeleccionadas([]);
    setBusquedaFactura("");
    setMostrarMovimientos(false);
    setMovsAdelanto([]);
    setMovsRemanente([]);
    Promise.all([
      getProveedoresFactoring(pais),
      getMovimientosBancarios(pais, { tipo: "ingreso" }),
      getCuentasBancarias(pais),
      client
        .from("facturas_sii_emitidas")
        .select("id, folio, razon_social_receptor, monto_total, fecha_emision")
        .eq("pais", pais)
        .is("estado_factoring", null)
        .order("fecha_emision", { ascending: false })
        .limit(200),
    ])
      .then(([provs, movs, ctas, facturasRes]) => {
        setProveedores(provs);
        setMovimientos(movs);
        setCuentas(ctas);
        setFacturasSII(facturasRes?.data || []);
      })
      .catch(() => toast.error("Error cargando datos"));
  }, [isOpen, pais, hoy]);

  const handleProveedorChange = (id) => {
    const prov = proveedores.find((p) => p.id === parseInt(id, 10));
    setForm((prev) => ({
      ...prev,
      proveedor_factoring_id: id,
      tasa_mora: prov?.tasa_mora ? (prov.tasa_mora * 100).toFixed(2) : "",
      pct_anticipo: prov?.pct_anticipo_habitual ? (prov.pct_anticipo_habitual * 100).toFixed(1) : "95",
    }));
  };

  const handleGuardar = async () => {
    if (!form.proveedor_factoring_id) return toast.error("Seleccioná un proveedor");
    if (!montoF || montoF <= 0) return toast.error("Ingresá el monto de la factura");
    if (!pctAnticipo) return toast.error("Ingresá el % de anticipo");
    if (!form.fecha_operacion) return toast.error("Ingresá la fecha de operación");
    if (modo === "liquidada" && !form.fecha_pago_real) return toast.error("Ingresá la fecha de pago real");

    setLoading(true);
    try {
      const payload = {
        pais,
        proveedor_factoring_id: parseInt(form.proveedor_factoring_id, 10),
        referencia_proveedor: form.referencia_proveedor.trim() || null,
        monto_factura: montoF,
        pct_anticipo: pctAnticipo,
        capital_adelantado: capitalAdelantado,
        tasa: tasaF,
        tasa_mora: tasaMoraF || null,
        plazo_dias: plazoDias || null,
        costo_financiero: diferenciaPrecio,
        comision_proveedor: comisionProveedor,
        giro_a_recibir: giroARecibir,
        fecha_operacion: form.fecha_operacion,
        fecha_cobro_final: form.fecha_cobro_final || null,
        estado: modo,
        created_by: userId || null,
        cuenta_bancaria_id: form.cuenta_bancaria_id ? parseInt(form.cuenta_bancaria_id, 10) : null,
      };

      if (form.giro_depositado && Number(form.giro_depositado) !== giroARecibir) {
        payload.giro_depositado = Number(form.giro_depositado);
      }

      if (modo === "liquidada") {
        payload.fecha_pago_real = form.fecha_pago_real;
      }

      // Compat: si hay exactamente 1 factura, también seteamos la FK directa
      // (operaciones viejas usan factura_sii_emitida_id sin tabla puente).
      // Si hay N facturas, FK directa queda null y todo va por la tabla puente.
      if (facturasSeleccionadas.length === 1) {
        payload.factura_sii_emitida_id = facturasSeleccionadas[0].id;
      }

      const nuevaOp = await crearOperacionFactoringHistorica(payload);

      // Tabla puente + UPDATE estado_factoring en cada factura vinculada.
      if (facturasSeleccionadas.length > 0) {
        await vincularFacturasAOperacion(
          nuevaOp.id,
          facturasSeleccionadas.map((f) => ({ id: f.id, monto_total: Number(f.monto_total) })),
          modo === "liquidada" ? "liquidada" : "cedida",
          {
            fecha_pago_real: payload.fecha_pago_real,
            fecha_cobro_final: payload.fecha_cobro_final,
            fecha_operacion: payload.fecha_operacion,
          }
        );
      }

      // Tabla puente de movimientos bancarios (N por operación, separados por tipo).
      const todosMovs = [
        ...movsAdelanto.map((m) => ({ id: m.id, tipo: "adelanto" })),
        ...movsRemanente.map((m) => ({ id: m.id, tipo: "remanente" })),
      ];
      if (todosMovs.length > 0) {
        await vincularMovimientosAOperacion(nuevaOp.id, todosMovs);
      }

      toast.success(modo === "liquidada" ? "Operación histórica registrada" : "Operación registrada");
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Selector inline reutilizable de movimientos bancarios (multi-select).
  // selected: array de movs ya elegidos en este selector; excluir: movs elegidos en el otro
  // (para evitar que un mismo mov aparezca como adelanto y remanente a la vez).
  const SelectorMovimientos = ({ label, selected, onChange, excluir }) => {
    const disponibles = movimientos.filter(
      (m) => !selected.some((s) => s.id === m.id) && !excluir.some((e) => e.id === m.id)
    );
    return (
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
        {selected.map((m) => (
          <div
            key={m.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "3px 10px",
              background: "#e8f4fd",
              borderRadius: 4,
              fontSize: "0.8rem",
              marginBottom: 3,
            }}
          >
            <span style={{ color: "#1a5276" }}>
              {m.fecha ? `${m.fecha.slice(8, 10)}/${m.fecha.slice(5, 7)}` : "—"} — {m.descripcion_raw?.slice(0, 35)} —{" "}
              {fmt(Math.abs(m.monto))}
            </span>
            <button
              type="button"
              onClick={() => onChange(selected.filter((s) => s.id !== m.id))}
              style={{
                background: "none",
                border: "none",
                color: "#c0392b",
                cursor: "pointer",
                fontSize: "0.8rem",
                padding: "0 4px",
              }}
            >
              ×
            </button>
          </div>
        ))}
        <select
          className="form-control form-control-sm"
          value=""
          onChange={(e) => {
            const id = parseInt(e.target.value, 10);
            if (!id) return;
            const mov = movimientos.find((m) => m.id === id);
            if (mov) onChange([...selected, mov]);
          }}
        >
          <option value="">{selected.length === 0 ? "Sin vincular" : "+ Agregar otro movimiento"}</option>
          {disponibles.map((m) => (
            <option key={m.id} value={m.id}>
              {`${m.fecha ? `${m.fecha.slice(8, 10)}/${m.fecha.slice(5, 7)}` : "—"} — ${m.descripcion_raw?.slice(0, 40)} — ${fmt(Math.abs(m.monto))}`}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Nueva operación de factoring</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { key: "activa", label: "Operación nueva" },
            { key: "liquidada", label: "Histórica (ya liquidada)" },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModo(m.key)}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "1px solid #dee2e6",
                borderRadius: 6,
                cursor: "pointer",
                background: modo === m.key ? "#2c3e50" : "transparent",
                color: modo === m.key ? "#fff" : "#666",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Vinculación a facturas SII — múltiple */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Facturas SII emitidas (opcional)
            <span style={{ fontSize: "0.72rem", fontWeight: 400, color: "#888", marginLeft: 6 }}>
              — podés vincular una o más
            </span>
          </label>

          {facturasSeleccionadas.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {facturasSeleccionadas.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 10px",
                    background: "#e8f4fd",
                    borderRadius: 4,
                    fontSize: "0.8rem",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ color: "#27ae60", fontWeight: 600 }}>
                    ✓ Folio {f.folio} — {f.razon_social_receptor}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFacturasSeleccionadas((prev) => prev.filter((x) => x.id !== f.id))}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#c0392b",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      padding: "0 4px",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar por folio o cliente..."
            value={busquedaFactura}
            onChange={(e) => setBusquedaFactura(e.target.value)}
            style={{ marginBottom: 6 }}
          />

          {busquedaFactura.length >= 2 && (
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 4,
                maxHeight: 160,
                overflowY: "auto",
                background: "#fff",
              }}
            >
              {(() => {
                const q = busquedaFactura.toLowerCase();
                const matches = facturasSII.filter((f) => {
                  if (facturasSeleccionadas.some((x) => x.id === f.id)) return false;
                  return String(f.folio).includes(q) || (f.razon_social_receptor || "").toLowerCase().includes(q);
                });
                if (matches.length === 0) {
                  return <div style={{ padding: "10px 12px", color: "#999", fontSize: "0.82rem" }}>Sin resultados</div>;
                }
                return matches.slice(0, 20).map((f) => (
                  <div
                    key={f.id}
                    onClick={() => {
                      setFacturasSeleccionadas((prev) => [...prev, f]);
                      setBusquedaFactura("");
                    }}
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      Folio {f.folio} — {f.razon_social_receptor}
                    </span>
                    <span style={{ color: "#888", fontSize: "0.78rem" }}>{f.fecha_emision?.slice(0, 7)}</span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Proveedor de factoring
            </label>
            <select
              className="form-control form-control-sm"
              value={form.proveedor_factoring_id}
              onChange={(e) => handleProveedorChange(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Nº operación proveedor (opcional)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 406591"
              value={form.referencia_proveedor}
              onChange={(e) => setForm((p) => ({ ...p, referencia_proveedor: e.target.value }))}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: modo === "liquidada" ? "1fr 1fr 1fr" : "1fr 1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha operación
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_operacion}
              onChange={(e) => setForm((p) => ({ ...p, fecha_operacion: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha vencimiento
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_cobro_final}
              onChange={(e) => setForm((p) => ({ ...p, fecha_cobro_final: e.target.value }))}
            />
          </div>
          {modo === "liquidada" && (
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Fecha pago real *
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.fecha_pago_real}
                onChange={(e) => setForm((p) => ({ ...p, fecha_pago_real: e.target.value }))}
              />
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Monto factura
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 6.217.750"
              value={
                montoAutoCalculado != null
                  ? formatMontoDisplay(montoAutoCalculado, pais)
                  : formatMontoDisplay(form.monto_factura, pais)
              }
              onChange={(e) => {
                if (montoAutoCalculado != null) return;
                setForm((p) => ({ ...p, monto_factura: parseMontoInput(e.target.value, monedaPrincipal) }));
              }}
              readOnly={montoAutoCalculado != null}
              style={{
                background: montoAutoCalculado != null ? "#f0faf4" : undefined,
                borderColor: montoAutoCalculado != null ? "#27ae60" : undefined,
                fontWeight: montoAutoCalculado != null ? 600 : undefined,
                color: montoAutoCalculado != null ? "#198754" : undefined,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              % Anticipo
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 95"
              value={form.pct_anticipo}
              onChange={(e) => setForm((p) => ({ ...p, pct_anticipo: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Tasa mensual (%)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 2,67"
              value={form.tasa}
              onChange={(e) => setForm((p) => ({ ...p, tasa: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Tasa mora (%)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 3,79"
              value={form.tasa_mora}
              onChange={(e) => setForm((p) => ({ ...p, tasa_mora: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Comisión proveedor ($)
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej: 48.790"
              value={formatMontoDisplay(form.comision_proveedor, pais)}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  comision_proveedor: parseMontoInput(e.target.value, monedaPrincipal),
                }))
              }
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Cuenta bancaria destino
            </label>
            <select
              className="form-control form-control-sm"
              value={form.cuenta_bancaria_id}
              onChange={(e) => setForm((p) => ({ ...p, cuenta_bancaria_id: e.target.value }))}
            >
              <option value="">Seleccionar...</option>
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>
                  {`${c.bancos?.nombre || "Banco"} ${c.moneda} — ${c.numero_cuenta}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {montoF > 0 && pctAnticipo > 0 && (
          <div
            style={{
              background: "#fff3cd",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 12,
              fontSize: "0.82rem",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", color: "#666" }}>
              <span>Capital adelantado ({form.pct_anticipo}%):</span>
              <span>{fmt(capitalAdelantado)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#666", marginTop: 4 }}>
              <span>Plazo:</span>
              <span>{plazoDias} días</span>
            </div>
            <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Diferencia precio:</span>
              <span style={{ color: "#c0392b" }}>{fmt(diferenciaPrecio)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span>Comisión:</span>
              <span style={{ color: "#c0392b" }}>{fmt(comisionProveedor)}</span>
            </div>
            <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "0.95rem" }}>
              <span>GIRO A RECIBIR:</span>
              <span style={{ color: "#2e7d32" }}>{fmt(giroARecibir)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span>Excedentes:</span>
              <span style={{ color: "#2980b9" }}>{fmt(excedentes)}</span>
            </div>
          </div>
        )}

        {modo === "liquidada" && (
          <div style={{ marginTop: 12 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "#555",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={mostrarMovimientos}
                onChange={(e) => setMostrarMovimientos(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              Vincular movimientos bancarios
            </label>

            {mostrarMovimientos && (
              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "12px 14px",
                  background: "#f8f9fa",
                }}
              >
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Giro depositado real ($) — si difiere del calculado
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={`Calculado: ${fmt(giroARecibir)} — dejá vacío si coincide`}
                    value={formatMontoDisplay(form.giro_depositado, pais)}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        giro_depositado: parseMontoInput(e.target.value, monedaPrincipal),
                      }))
                    }
                  />
                </div>

                <SelectorMovimientos
                  label="Movimientos bancarios — Giro (adelanto)"
                  selected={movsAdelanto}
                  onChange={setMovsAdelanto}
                  excluir={movsRemanente}
                />
                <SelectorMovimientos
                  label="Movimientos bancarios — Excedente (remanente)"
                  selected={movsRemanente}
                  onChange={setMovsRemanente}
                  excluir={movsAdelanto}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={loading}>
          {loading ? "Guardando..." : modo === "liquidada" ? "Registrar histórica" : "Registrar operación"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Popover inline en la celda "Comisión DTE": vincular/desvincular facturas de comisión sin
// abrir el modal Editar. Misma lógica/tabla puente que el modal — ambos comparten state vía
// onChange (refresca comisionesMap en el padre). Click fuera cierra.
const ComisionDtePopover = ({ op, pais, monedaPrincipal, comisiones, onChange, onClose }) => {
  const { formatAmount } = useCurrency();
  const [disponibles, setDisponibles] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const rutFactor = RUT_COMISION_POR_FACTOR[op.proveedor_factoring_id];
  const comisionRef = Number(op.comision_proveedor || 0);

  const cargarDisponibles = async () => {
    if (!rutFactor) return;
    try {
      setDisponibles(await getFacturasComisionDisponibles(pais, rutFactor));
    } catch {
      setDisponibles([]);
    }
  };

  useEffect(() => {
    cargarDisponibles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click fuera → cerrar.
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleVincular = async (facturaId) => {
    setLoading(true);
    try {
      await vincularComisionFactoring(op.id, facturaId);
      await onChange(op.id);
      await cargarDisponibles();
    } catch {
      toast.error("Error al vincular comisión");
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincular = async (filaId) => {
    setLoading(true);
    try {
      await desvincularComisionFactoring(filaId);
      await onChange(op.id);
      await cargarDisponibles();
    } catch {
      toast.error("Error al desvincular comisión");
    } finally {
      setLoading(false);
    }
  };

  const ordenadas = [...disponibles]
    .filter((f) => {
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return (
        String(f.folio).includes(q) || String(Math.round(Number(f.monto_total) || 0)).includes(q.replace(/\D/g, ""))
      );
    })
    .sort((a, b) => {
      const sa = Math.abs(Number(a.monto_total) - comisionRef) <= 100 ? 0 : 1;
      const sb = Math.abs(Number(b.monto_total) - comisionRef) <= 100 ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (b.fecha_emision || "").localeCompare(a.fecha_emision || "");
    });

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        marginTop: 4,
        width: 290,
        background: "#fff",
        border: "1px solid #ced4da",
        borderRadius: 6,
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        zIndex: 1000,
        padding: 8,
        textAlign: "left",
        whiteSpace: "normal",
      }}
    >
      {comisiones.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {comisiones.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 8px",
                marginBottom: 3,
                borderRadius: 4,
                background: "#f0f9f4",
                border: "1px solid #198754",
                fontSize: "0.76rem",
              }}
            >
              <span style={{ color: "#198754", fontWeight: 600 }}>
                ✓ Folio {c.factura?.folio ?? c.factura_sii_recibida_id}
                {c.factura?.monto_total != null ? ` · ${formatAmount(c.factura.monto_total, monedaPrincipal)}` : ""}
              </span>
              <button
                type="button"
                onClick={() => handleDesvincular(c.id)}
                disabled={loading}
                style={{
                  border: "1px solid #c62828",
                  background: "none",
                  borderRadius: 4,
                  color: "#c62828",
                  fontSize: "0.72rem",
                  padding: "1px 7px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {!rutFactor ? (
        <div style={{ fontSize: "0.74rem", color: "#999", padding: 4 }}>Proveedor sin RUT mapeado.</div>
      ) : (
        <>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar folio o monto..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ marginBottom: 6, fontSize: "0.78rem" }}
          />
          <div style={{ maxHeight: 170, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
            {ordenadas.length === 0 ? (
              <div style={{ padding: 10, textAlign: "center", color: "#999", fontSize: "0.76rem" }}>
                Sin facturas disponibles
              </div>
            ) : (
              ordenadas.map((f) => {
                const sugerida = Math.abs(Number(f.monto_total) - comisionRef) <= 100;
                return (
                  <div
                    key={f.id}
                    onClick={() => !loading && handleVincular(f.id)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "5px 8px",
                      borderBottom: "1px solid #f0f0f0",
                      fontSize: "0.76rem",
                      cursor: loading ? "not-allowed" : "pointer",
                      background: sugerida ? "#f0f9f4" : "transparent",
                    }}
                  >
                    <span>
                      {formatFecha(f.fecha_emision)} · Folio {f.folio}
                      {sugerida && (
                        <span style={{ marginLeft: 5, color: "#198754", fontSize: "0.68rem", fontWeight: 600 }}>
                          ≈ comisión
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: 600, color: "#c0392b" }}>
                      {formatAmount(f.monto_total, monedaPrincipal)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Factoring = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const monedaPrincipal = pais === "Chile" ? "CLP" : "PEN";

  const [operaciones, setOperaciones] = useState([]);
  // Map<operacion_factoring_id, Array<{id, factura}>> — comisiones (DTE recibido) por operación.
  const [comisionesMap, setComisionesMap] = useState(() => new Map());
  // id de la operación cuyo popover inline de comisión está abierto (o null).
  const [comisionPopoverOpId, setComisionPopoverOpId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroProveedor, setFiltroProveedor] = useState("todos"); // proveedor_factoring_id o "todos"
  const [filtroMes, setFiltroMes] = useState("todos"); // "YYYY-MM" o "todos"
  const [filtroFolio, setFiltroFolio] = useState("todos"); // folio exacto o "todos"
  const [liquidarModal, setLiquidarModal] = useState(null);
  const [moraModal, setMoraModal] = useState(null);
  const [editarModal, setEditarModal] = useState(null);
  const [nuevaOpModal, setNuevaOpModal] = useState(false);
  const [vincularModal, setVincularModal] = useState(null);
  // Menú kebab de acciones por fila: { op, top, right } o null (uno solo a la vez).
  const [menuAcciones, setMenuAcciones] = useState(null);
  const menuAccionesRef = useRef(null);
  const [proveedoresPanel, setProveedoresPanel] = useState([]);
  const [proveedoresPanelOpen, setProveedoresPanelOpen] = useState(false);
  const [editandoProveedor, setEditandoProveedor] = useState(null);
  const [savingProveedor, setSavingProveedor] = useState(false);

  const cargar = async () => {
    if (!pais) return;
    setLoading(true);
    try {
      const [data, { data: comisionesData }] = await Promise.all([
        getOperacionesFactoring(pais),
        client.from("operaciones_factoring_comisiones").select(
          `id, operacion_factoring_id, factura_sii_recibida_id,
             factura:facturas_sii_recibidas(id, folio, monto_total)`
        ),
      ]);
      setOperaciones(data);
      const m = new Map();
      for (const c of comisionesData || []) {
        const arr = m.get(c.operacion_factoring_id) || [];
        arr.push(c);
        m.set(c.operacion_factoring_id, arr);
      }
      setComisionesMap(m);
    } catch (err) {
      console.error("Error cargando factoring:", err);
      toast.error("Error cargando operaciones de factoring");
    } finally {
      setLoading(false);
    }
  };

  // Refresca las comisiones de UNA operación en el map (sin reload completo). Lo usan el
  // popover inline y el modal Editar para reflejar vínculos al instante.
  const refreshComisionesOp = async (opId) => {
    const filas = await getComisionesFactoring(opId);
    setComisionesMap((prev) => {
      const m = new Map(prev);
      m.set(opId, filas);
      return m;
    });
  };

  const cargarProveedores = async () => {
    if (!pais) return;
    try {
      const data = await getProveedoresFactoring(pais);
      setProveedoresPanel(data);
    } catch {
      toast.error("Error cargando proveedores");
    }
  };

  const handleGuardarProveedor = async () => {
    if (!editandoProveedor) return;
    setSavingProveedor(true);
    try {
      await actualizarProveedorFactoring(editandoProveedor.id, {
        tasa_mora: editandoProveedor.tasa_mora !== "" ? parseFloat(editandoProveedor.tasa_mora) / 100 : null,
        pct_anticipo_habitual:
          editandoProveedor.pct_anticipo_habitual !== ""
            ? parseFloat(editandoProveedor.pct_anticipo_habitual) / 100
            : null,
      });
      toast.success("Proveedor actualizado");
      setEditandoProveedor(null);
      cargarProveedores();
    } catch (err) {
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setSavingProveedor(false);
    }
  };

  useEffect(() => {
    cargar();
    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  // Cierre del menú kebab de acciones: click fuera + Escape. El botón kebab (data-kebab)
  // maneja su propio toggle, así que sus clicks se ignoran acá.
  useEffect(() => {
    if (!menuAcciones) return undefined;
    const onDown = (e) => {
      if (e.target.closest("[data-kebab]")) return;
      if (menuAccionesRef.current && !menuAccionesRef.current.contains(e.target)) setMenuAcciones(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuAcciones(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuAcciones]);

  // Operaciones con mora_pendiente derivada de la subcolección embed.
  const operacionesConMora = useMemo(
    () =>
      operaciones.map((op) => ({
        ...op,
        mora_pendiente: (op.moras || [])
          .filter((m) => m.estado === "pendiente")
          .reduce((s, m) => s + Number(m.monto_mora || 0), 0),
      })),
    [operaciones]
  );

  // Opciones de proveedor derivadas de las operaciones existentes (id + nombre del embed).
  const proveedorOpciones = useMemo(() => {
    const map = new Map();
    for (const o of operaciones) {
      if (o.proveedor_factoring_id != null && !map.has(o.proveedor_factoring_id)) {
        map.set(o.proveedor_factoring_id, o.proveedor?.nombre || `#${o.proveedor_factoring_id}`);
      }
    }
    return [...map.entries()].map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [operaciones]);

  // Opciones de mes (YYYY-MM) derivadas de fecha_operacion, descendente.
  const mesOpciones = useMemo(() => {
    const set = new Set();
    for (const o of operaciones) {
      if (o.fecha_operacion) set.add(o.fecha_operacion.substring(0, 7));
    }
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [operaciones]);

  // Opciones de folio derivadas de la factura emitida de cada operación, descendente.
  const folioOpciones = useMemo(() => {
    const set = new Set();
    for (const o of operaciones) {
      if (o.factura?.folio != null) set.add(String(o.factura.folio));
    }
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [operaciones]);

  const operacionesFiltradas = useMemo(
    () =>
      operacionesConMora.filter((o) => {
        if (filtroEstado !== "todos" && o.estado !== filtroEstado) return false;
        if (filtroProveedor !== "todos" && o.proveedor_factoring_id !== Number(filtroProveedor)) return false;
        if (filtroMes !== "todos" && (o.fecha_operacion || "").substring(0, 7) !== filtroMes) return false;
        if (filtroFolio !== "todos" && String(o.factura?.folio ?? "") !== filtroFolio) return false;
        return true;
      }),
    [operacionesConMora, filtroEstado, filtroProveedor, filtroMes, filtroFolio]
  );

  const resumen = useMemo(() => {
    const totalCedido = operaciones.reduce((s, o) => s + Number(o.monto_factura || 0), 0);
    // Costo real = diferencia precio (costo_financiero) + comisión fija del proveedor.
    // El IVA del financiamiento es impuesto, no costo: se excluye del total.
    const totalCosto = operaciones.reduce(
      (s, o) => s + Number(o.costo_financiero || 0) + Number(o.comision_proveedor || 0),
      0
    );
    const activas = operaciones.filter((o) => o.estado === "activa").length;
    const liquidadas = operaciones.filter((o) => o.estado === "liquidada").length;
    const anuladas = operaciones.filter((o) => o.estado === "anulada").length;
    return { totalCedido, totalCosto, activas, liquidadas, anuladas };
  }, [operaciones]);

  const handleAnular = async (op) => {
    const mensaje =
      `¿Anular operación de factoring para folio ${op.factura?.folio}?\n` +
      "La factura volverá a aparecer en «Por cobrar».";
    if (!window.confirm(mensaje)) return;
    try {
      await anularOperacionFactoring(op.id);
      toast.success("Operación anulada — factura liberada");
      await cargar();
    } catch (err) {
      console.error("Error anulando:", err);
      toast.error("Error al anular");
    }
  };

  const handleEliminar = async (op) => {
    const mensaje =
      `¿Eliminar definitivamente la operación anulada del Folio ${op.factura?.folio}?\n` +
      "Esta acción no se puede deshacer.";
    if (!window.confirm(mensaje)) return;
    try {
      await eliminarOperacionFactoring(op.id);
      toast.success("Operación eliminada");
      await cargar();
    } catch (err) {
      console.error("Error eliminando:", err);
      toast.error("Error al eliminar");
    }
  };

  // Mantengo el handler de liquidar viejo solo para fallback; el flujo principal pasa
  // por LiquidarFactoringModal. Preserva el setState optimista para no recargar todo.
  // eslint-disable-next-line no-unused-vars
  const handleLiquidarLegacy = async (op, fechaPagoReal) => {
    try {
      await liquidarOperacionFactoring(op, { fechaPagoReal });
      toast.success("Operación liquidada");
      await cargar();
    } catch (err) {
      console.error("Error liquidando:", err);
      toast.error("Error al liquidar la operación");
    }
  };

  const renderEstadoBadge = (estado) => {
    if (estado === "activa") {
      return <span className={`${styles.estadoBadge} ${styles.estadoActiva}`}>Activa</span>;
    }
    if (estado === "liquidada") {
      return <span className={`${styles.estadoBadge} ${styles.estadoLiquidada}`}>Liquidada</span>;
    }
    return (
      <span className={styles.estadoBadge} style={{ background: "#e0e0e0", color: "#555" }}>
        Anulada
      </span>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Header
        centerContent={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <select
              className="form-select form-select-sm"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Estado"
            >
              {ESTADO_OPCIONES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Proveedor"
            >
              <option value="todos">Proveedor</option>
              {proveedorOpciones.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Mes de operación"
            >
              <option value="todos">Mes</option>
              {mesOpciones.map((m) => {
                const [y, mm] = m.split("-");
                return (
                  <option key={m} value={m}>
                    {MESES_NOMBRES[Number(mm) - 1]} {y}
                  </option>
                );
              })}
            </select>
            <select
              className="form-select form-select-sm"
              value={filtroFolio}
              onChange={(e) => setFiltroFolio(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Folio"
            >
              <option value="todos">Folio</option>
              {folioOpciones.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <button type="button" className={styles.btnNuevaOp} onClick={() => setNuevaOpModal(true)}>
          + Nueva operación
        </button>
      </Header>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div className={styles.cardsSection}>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Total cedido</div>
                <div className={styles.cardValue}>{formatAmount(resumen.totalCedido, monedaPrincipal)}</div>
              </div>
              <div className={`${styles.card} ${styles.cardRed}`}>
                <div className={styles.cardLabel}>Costo acumulado</div>
                <div className={styles.cardValue}>{formatAmount(resumen.totalCosto, monedaPrincipal)}</div>
              </div>
              <div className={`${styles.card} ${styles.cardOrange}`}>
                <div className={styles.cardLabel}>Operaciones activas</div>
                <div className={styles.cardValue}>{resumen.activas}</div>
              </div>
              <div className={`${styles.card} ${styles.cardGreen}`}>
                <div className={styles.cardLabel}>Liquidadas</div>
                <div className={styles.cardValue}>{resumen.liquidadas}</div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Anuladas</div>
                <div className={styles.cardValue}>{resumen.anuladas}</div>
              </div>
            </div>

            {operacionesFiltradas.length === 0 ? (
              <div className={styles.empty}>Sin operaciones de factoring registradas.</div>
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.tableResponsive}>
                  <table className={`table table-hover table-striped ${styles.table}`}>
                    <thead>
                      <tr>
                        <th className={styles.colFecha}>Fecha</th>
                        <th className={styles.colFolio}>Folio</th>
                        <th className={styles.colRef}>Ref.</th>
                        <th className={styles.colCliente}>Cliente</th>
                        <th className={styles.colProveedor}>Proveedor</th>
                        <th className={styles.colMonto}>Monto</th>
                        <th className={styles.colPct}>Anticipo</th>
                        <th className={styles.colTasa}>Tasa</th>
                        <th className={styles.colPlazo}>Plazo</th>
                        <th className={styles.colGiro}>Giro recibido</th>
                        <th className={styles.colExcedente}>Excedentes</th>
                        <th className={styles.colComisionDte}>Comisión DTE</th>
                        <th className={styles.colVenc}>Vencimiento</th>
                        <th className={styles.colEstado}>Estado</th>
                        <th className={styles.colAccion}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operacionesFiltradas.map((op) => {
                        const vinc = op.facturas_vinculadas || [];
                        const foliosArr = vinc.map((fv) => fv.factura?.folio).filter(Boolean);
                        const foliosTxt = foliosArr.length > 0 ? foliosArr.join(" + ") : op.factura?.folio || "";
                        const nombresArr = [
                          ...new Set(vinc.map((fv) => fv.factura?.razon_social_receptor).filter(Boolean)),
                        ];
                        const nombresTxt =
                          nombresArr.length > 0 ? nombresArr.join(" / ") : op.factura?.razon_social_receptor || "";
                        // Punto rojo del giro: solo cuando el depositado difiere del calculado.
                        const giroDifiere =
                          op.giro_depositado != null && Number(op.giro_depositado) !== Number(op.giro_a_recibir);
                        return (
                          <tr key={op.id}>
                            <td className={styles.colFecha}>{formatFecha(op.fecha_operacion)}</td>
                            <td className={styles.colFolio}>{foliosTxt || "—"}</td>
                            <td className={styles.colRef} title={op.referencia_proveedor || ""}>
                              {op.referencia_proveedor || "—"}
                            </td>
                            <td className={styles.colCliente} title={nombresTxt}>
                              {nombresTxt || "—"}
                            </td>
                            <td className={styles.colProveedor}>{op.proveedor?.nombre || "—"}</td>
                            <td className={styles.colMonto}>{formatAmount(op.monto_factura, monedaPrincipal)}</td>
                            <td className={styles.colPct}>
                              {op.pct_anticipo ? `${(op.pct_anticipo * 100).toFixed(1)}%` : "—"}
                            </td>
                            <td className={styles.colTasa}>{op.tasa ? `${(op.tasa * 100).toFixed(2)}%` : "—"}</td>
                            <td className={styles.colPlazo}>{op.plazo_dias || "—"}</td>
                            <td className={styles.colGiro}>
                              <span style={{ color: "#198754", fontWeight: 600 }}>
                                {op.giro_depositado || op.giro_a_recibir
                                  ? formatAmount(op.giro_depositado || op.giro_a_recibir, monedaPrincipal)
                                  : "—"}
                              </span>
                              {giroDifiere && (
                                <span className={styles.tooltipWrapper}>
                                  <span className={styles.tooltipDot}>●</span>
                                  <span className={styles.tooltipBox}>
                                    {`Depositado: ${formatAmount(Number(op.giro_depositado), monedaPrincipal)}`}
                                    <br />
                                    {`Calculado: ${formatAmount(Number(op.giro_a_recibir), monedaPrincipal)}`}
                                    <br />
                                    {`Diferencia: ${formatAmount(
                                      Number(op.giro_depositado) - Number(op.giro_a_recibir),
                                      monedaPrincipal
                                    )}`}
                                  </span>
                                </span>
                              )}
                            </td>
                            <td className={styles.colExcedente} style={{ color: "#1d4e89" }}>
                              {formatAmount(
                                op.monto_remanente ?? Number(op.monto_factura) - Number(op.capital_adelantado),
                                monedaPrincipal
                              )}
                            </td>
                            <td className={styles.colComisionDte} onClick={(e) => e.stopPropagation()}>
                              <span style={{ position: "relative", display: "inline-block" }}>
                                <span
                                  onClick={() => setComisionPopoverOpId(comisionPopoverOpId === op.id ? null : op.id)}
                                  style={{ cursor: "pointer" }}
                                  title="Vincular factura de comisión"
                                >
                                  {(comisionesMap.get(op.id) || []).length > 0 ? (
                                    <span style={{ color: "#198754", fontWeight: 600, fontSize: "0.78rem" }}>
                                      {(comisionesMap.get(op.id) || [])
                                        .map((c) => `✓ ${c.factura?.folio ?? c.factura_sii_recibida_id}`)
                                        .join(", ")}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#c0392b", fontStyle: "italic", fontSize: "0.75rem" }}>
                                      pendiente
                                    </span>
                                  )}
                                </span>
                                {comisionPopoverOpId === op.id && (
                                  <ComisionDtePopover
                                    op={op}
                                    pais={pais}
                                    monedaPrincipal={monedaPrincipal}
                                    comisiones={comisionesMap.get(op.id) || []}
                                    onChange={refreshComisionesOp}
                                    onClose={() => setComisionPopoverOpId(null)}
                                  />
                                )}
                              </span>
                            </td>
                            <td className={styles.colVenc}>{formatFecha(op.fecha_cobro_final)}</td>
                            <td className={styles.colEstado}>{renderEstadoBadge(op.estado)}</td>
                            <td className={styles.colAccion} onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                data-kebab="true"
                                className="btn btn-sm btn-light"
                                title="Acciones"
                                aria-label="Acciones"
                                onClick={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setMenuAcciones((prev) =>
                                    prev?.op?.id === op.id
                                      ? null
                                      : { op, top: r.bottom + 2, right: window.innerWidth - r.right }
                                  );
                                }}
                              >
                                <BsThreeDotsVertical />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Panel proveedores de factoring */}
            <div style={{ marginTop: 24 }}>
              <button
                type="button"
                onClick={() => setProveedoresPanelOpen((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#2c3e50",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  padding: "4px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{proveedoresPanelOpen ? "▾" : "▸"}</span>
                Proveedores de factoring
              </button>

              {proveedoresPanelOpen && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.83rem" }}>
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "30%" }}>Proveedor</th>
                        <th style={{ width: "17%", textAlign: "center" }}>Tasa mora (%)</th>
                        <th style={{ width: "18%", textAlign: "center" }}>% Anticipo habitual</th>
                        <th style={{ width: "15%", textAlign: "center" }}>Tipo</th>
                        <th style={{ width: "20%", textAlign: "center" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proveedoresPanel.map((prov) => {
                        const esEditando = editandoProveedor?.id === prov.id;
                        return (
                          <tr key={prov.id}>
                            <td style={{ fontWeight: 600 }}>{prov.nombre}</td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 70, margin: "0 auto", textAlign: "center" }}
                                  value={editandoProveedor.tasa_mora}
                                  onChange={(e) => setEditandoProveedor((p) => ({ ...p, tasa_mora: e.target.value }))}
                                  placeholder="ej: 3.27"
                                />
                              ) : prov.tasa_mora ? (
                                `${(prov.tasa_mora * 100).toFixed(2)}%`
                              ) : (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: 70, margin: "0 auto", textAlign: "center" }}
                                  value={editandoProveedor.pct_anticipo_habitual}
                                  onChange={(e) =>
                                    setEditandoProveedor((p) => ({ ...p, pct_anticipo_habitual: e.target.value }))
                                  }
                                  placeholder="ej: 95"
                                />
                              ) : prov.pct_anticipo_habitual ? (
                                `${(prov.pct_anticipo_habitual * 100).toFixed(1)}%`
                              ) : (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center", color: "#888" }}>{prov.tipo || "—"}</td>
                            <td style={{ textAlign: "center" }}>
                              {esEditando ? (
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                  <button
                                    type="button"
                                    onClick={handleGuardarProveedor}
                                    disabled={savingProveedor}
                                    style={{
                                      background: "#198754",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 4,
                                      padding: "3px 10px",
                                      fontSize: "0.75rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {savingProveedor ? "..." : "Guardar"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditandoProveedor(null)}
                                    style={{
                                      background: "none",
                                      border: "1px solid #999",
                                      borderRadius: 4,
                                      padding: "3px 8px",
                                      fontSize: "0.75rem",
                                      cursor: "pointer",
                                      color: "#666",
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditandoProveedor({
                                      ...prov,
                                      tasa_mora: prov.tasa_mora ? (prov.tasa_mora * 100).toFixed(2) : "",
                                      pct_anticipo_habitual: prov.pct_anticipo_habitual
                                        ? (prov.pct_anticipo_habitual * 100).toFixed(1)
                                        : "",
                                    })
                                  }
                                  style={{
                                    background: "none",
                                    border: "1px solid #0d6efd",
                                    borderRadius: 4,
                                    padding: "3px 10px",
                                    fontSize: "0.75rem",
                                    color: "#0d6efd",
                                    cursor: "pointer",
                                  }}
                                >
                                  Editar
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Menú kebab de acciones (position fixed → escapa el doble overflow de la tabla).
          Items filtrados solo por estado de la operación (factoring no tiene gates de rol). */}
      {menuAcciones &&
        (() => {
          const op = menuAcciones.op;
          const tieneAdelantoVinc =
            !!op.movimiento_bancario_adelanto_id ||
            (op.movimientos_vinculados || []).some((mv) => mv.tipo === "adelanto");
          const item = (label, onClick) => (
            <button
              type="button"
              className="dropdown-item"
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "6px 16px",
                border: "none",
                background: "none",
                fontSize: "0.85rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              onClick={() => {
                setMenuAcciones(null);
                onClick();
              }}
            >
              {label}
            </button>
          );
          return (
            <div
              ref={menuAccionesRef}
              style={{
                position: "fixed",
                top: menuAcciones.top,
                right: menuAcciones.right,
                zIndex: 2000,
                background: "#fff",
                border: "1px solid #dee2e6",
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                minWidth: 160,
                padding: "4px 0",
              }}
            >
              {op.estado === "activa" && (
                <>
                  {item("Liquidar", () => setLiquidarModal(op))}
                  {!tieneAdelantoVinc &&
                    item("Vincular", () =>
                      setVincularModal({
                        id: null,
                        tipo: "ingreso",
                        monto: op.giro_a_recibir || 0,
                        moneda: monedaPrincipal,
                        fecha: new Date().toISOString().split("T")[0],
                        descripcion_raw: `Factoring — ${op.factura?.razon_social_receptor || ""} Folio ${op.factura?.folio || ""}`,
                        pais,
                        _factoringOpId: op.id,
                        _factoringTipo: "giro",
                      })
                    )}
                  {item("Editar", () => setEditarModal(op))}
                  {item("Anular", () => handleAnular(op))}
                </>
              )}
              {op.estado === "liquidada" && item("+ Mora", () => setMoraModal(op))}
              {op.estado === "anulada" && item("Eliminar", () => handleEliminar(op))}
            </div>
          );
        })()}

      {liquidarModal && (
        <LiquidarFactoringModal
          isOpen={!!liquidarModal}
          onClose={() => setLiquidarModal(null)}
          operacion={liquidarModal}
          pais={pais}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setLiquidarModal(null);
            cargar();
          }}
        />
      )}

      {moraModal && (
        <MoraModal
          isOpen={!!moraModal}
          onClose={() => setMoraModal(null)}
          operacion={moraModal}
          pais={pais}
          userId={userId}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setMoraModal(null);
            cargar();
          }}
        />
      )}

      {editarModal && (
        <EditarOperacionModal
          isOpen={!!editarModal}
          onClose={() => setEditarModal(null)}
          operacion={editarModal}
          pais={pais}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setEditarModal(null);
            cargar();
          }}
          onComisionChange={() => cargar()}
        />
      )}

      {nuevaOpModal && (
        <NuevaOperacionModal
          isOpen={nuevaOpModal}
          onClose={() => setNuevaOpModal(false)}
          pais={pais}
          userId={userId}
          monedaPrincipal={monedaPrincipal}
          onSuccess={() => {
            setNuevaOpModal(false);
            cargar();
          }}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={() => {
            setVincularModal(null);
            cargar();
          }}
        />
      )}
    </div>
  );
};

export default Factoring;
